import { useForm } from "react-hook-form";
import { useState, useEffect, useMemo, useRef } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import YhMultiSelect from "../../../../../../ui/YhMultiSelect";
import { LABEL_MAP } from "./Box";

const parseOptionsList = (raw: any): string[] => {
  if (Array.isArray(raw)) {
    return raw.map((o) => String(o ?? "").trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(";")
      .map((o) => o.trim())
      .filter(Boolean);
  }
  return [];
};

/** Creatable option chips — type + Enter (also accepts ; paste). */
const OptionsCreatable = ({
  options,
  onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) => {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addFromText = (text: string) => {
    const parts = text
      .split(/[;\n,]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = [...options];
    for (const p of parts) {
      if (!next.includes(p)) next.push(p);
    }
    onChange(next);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    onChange(options.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-[#111827]">
        Options
      </label>
      <div
        className="flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-xl border border-[#E5E7EB] bg-white px-2.5 py-2 transition focus-within:border-[#841C39] focus-within:ring-2 focus-within:ring-[#841C39]/20"
        onClick={() => inputRef.current?.focus()}
      >
        {options.map((opt, idx) => (
          <span
            key={`${opt}-${idx}`}
            className="inline-flex max-w-full items-center gap-1 rounded-lg border border-[#E5E7EB] bg-[#F3F4F6] px-2 py-1 text-xs font-medium text-[#374151]"
          >
            <span className="truncate">{opt}</span>
            <button
              type="button"
              aria-label={`Remove ${opt}`}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(idx);
              }}
              className="ml-0.5 shrink-0 rounded text-[#9CA3AF] hover:text-[#DC2626]"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ";") {
              e.preventDefault();
              addFromText(draft);
            } else if (e.key === "Backspace" && !draft && options.length) {
              removeAt(options.length - 1);
            }
          }}
          onBlur={() => {
            if (draft.trim()) addFromText(draft);
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[;\n,]/.test(text)) {
              e.preventDefault();
              addFromText(text);
            }
          }}
          placeholder={
            options.length ? "Add another…" : "Type option and press Enter..."
          }
          className="min-w-[140px] flex-1 border-0 bg-transparent py-0.5 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF]"
        />
      </div>
      {!options.length ? (
        <p className="mt-1 text-[11px] text-[#DC2626]">
          Add at least one option.
        </p>
      ) : (
        <p className="mt-1 text-[11px] text-[#9CA3AF]">
          Press Enter to add. You can also paste{" "}
          <span className="font-medium">Option 1;Option 2</span>.
        </p>
      )}
    </div>
  );
};

const ChoiceCard = ({
  title,
  subtitle,
  selected,
  selectedClass,
  onClick,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  selectedClass: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition ${
      selected
        ? selectedClass
        : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#C7CBD6]"
    }`}
  >
    <span className="block text-sm font-semibold">{title}</span>
    <span
      className={`mt-0.5 block text-[11px] leading-snug ${
        selected ? "opacity-90" : "text-[#6B7280]"
      }`}
    >
      {subtitle}
    </span>
  </button>
);

type ConditionRule = {
  fieldId: string;
  value: string;
  operator: string;
  action: "show" | "hide" | "set_value";
  setValue?: string;
};

const emptyCondition = (): ConditionRule => ({
  fieldId: "",
  value: "",
  operator: "equals",
  action: "show",
  setValue: "",
});

const NO_PLACEHOLDER = new Set([
  "divider",
  "heading",
  "section",
  "button",
  "rating",
]);
const NO_REQUIRED = new Set(["divider", "section", "button"]);
const HAS_OPTIONS = new Set(["select", "radio", "checkbox"]);

const BuilderPage = (props: any) => {
  const [editFormData, setFormData] = useState({
    placeholder: "placeHolder",
    required: false,
    name: "name",
    type: "",
    options: [] as string[],
    sectionType: "normal" as "normal" | "step",
    access: "all" as "all" | "restricted",
    conditions: [] as ConditionRule[],
    conditionOperator: "AND" as "AND" | "OR",
    selectionMode: "single" as "single" | "multiple",
    projectScope: "all" as "all" | "selected",
    allowedProjectIds: [] as string[],
    ratingStyle: "emoji" as "emoji" | "stars",
    choiceStyle: "chips" as "chips" | "dropdown",
    allowOther: false,
  });
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const { register, handleSubmit, reset, formState } = useForm();
  const { errors } = formState;

  const otherFields = useMemo(
    () =>
      (props.items || []).filter(
        (item: any) =>
          item.id !== props.inputId &&
          item.type !== "section" &&
          item.type !== "divider" &&
          item.type !== "button"
      ),
    [props.items, props.inputId]
  );

  const patchItem = (partial: Record<string, any>) => {
    // Functional update so rapid typing cannot overwrite a previous keystroke
    // with a stale `props.items` snapshot.
    props.setItems((prev: any[]) =>
      (Array.isArray(prev) ? prev : []).map((item: any) =>
        item.id === props.inputId ? { ...item, ...partial } : item
      )
    );
  };

  const onSubmit = (data: any) => {
    if (
      HAS_OPTIONS.has(editFormData.type) &&
      editFormData.options.length === 0
    ) {
      return;
    }
    if (
      editFormData.type === "project" &&
      editFormData.projectScope === "selected" &&
      !editFormData.allowedProjectIds.length
    ) {
      return;
    }
    const updatedItems = props.items.map((item: any) =>
      item.id === props.inputId
        ? {
            ...item,
            placeholder: data.placeholder || "placeholder",
            required: !!data.required,
            name: data.name || "name",
            options: HAS_OPTIONS.has(editFormData.type)
              ? editFormData.options
              : item.options,
            conditions: editFormData.conditions,
            conditionOperator: editFormData.conditionOperator,
            ...(editFormData.type === "project"
              ? {
                  selectionMode: editFormData.selectionMode,
                  projectScope: editFormData.projectScope,
                  allowedProjectIds: editFormData.allowedProjectIds,
                }
              : {}),
          }
        : item
    );
    props.setItems(updatedItems);
    if (props.keepOpen) {
      reset({
        name: data.name,
        placeholder: data.placeholder,
        required: data.required,
      });
    } else {
      props.setInputId("");
      reset();
    }
  };

  useEffect(() => {
    if (props.inputId) {
      const currentItem = props.items.find(
        (item: any) => item.id === props.inputId
      );
      if (currentItem) {
        const options = parseOptionsList(currentItem.options);
        const access =
          currentItem.sectionAccess === "restricted" ||
          currentItem.access === "restricted"
            ? "restricted"
            : "all";
        setFormData({
          placeholder: currentItem.placeholder || "placeHolder",
          required: currentItem.required || false,
          name: currentItem.name || currentItem.label || "name",
          type: currentItem.type || "",
          options,
          sectionType:
            currentItem.sectionType === "step" ? "step" : "normal",
          access,
          conditions: Array.isArray(currentItem.conditions)
            ? currentItem.conditions
            : [],
          conditionOperator:
            currentItem.conditionOperator === "OR" ? "OR" : "AND",
          selectionMode:
            currentItem.selectionMode === "multiple" ? "multiple" : "single",
          projectScope:
            currentItem.projectScope === "selected" ? "selected" : "all",
          allowedProjectIds: Array.isArray(currentItem.allowedProjectIds)
            ? currentItem.allowedProjectIds.map(String)
            : [],
          ratingStyle:
            currentItem.ratingStyle === "stars" ? "stars" : "emoji",
          choiceStyle:
            currentItem.choiceStyle === "dropdown" ? "dropdown" : "chips",
          allowOther: !!currentItem.allowOther,
        });
        setConditionsOpen(
          Array.isArray(currentItem.conditions) &&
            currentItem.conditions.length > 0
        );
        reset({
          name: currentItem.name || currentItem.label || "name",
          placeholder: currentItem.placeholder || "placeholder",
          required: currentItem.required || false,
        });
      }
    } else {
      setFormData({
        placeholder: "placeHolder",
        required: false,
        name: "name",
        type: "",
        options: [],
        sectionType: "normal",
        access: "all",
        conditions: [],
        conditionOperator: "AND",
        selectionMode: "single",
        projectScope: "all",
        allowedProjectIds: [],
        ratingStyle: "emoji",
        choiceStyle: "chips",
        allowOther: false,
      });
      setConditionsOpen(false);
      reset();
    }
    // Only re-hydrate when selection changes — do not depend on `reset`
    // (react-hook-form can recreate it and cause an infinite render loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.inputId]);

  if (!props.inputId) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <p className="text-sm font-semibold text-[#111827]">No field selected</p>
        <p className="mt-1 text-xs text-[#6B7280]">
          Click Edit on a dropped field to configure it here.
        </p>
      </div>
    );
  }

  if (editFormData.type === "section") {
    return (
      <div className="flex h-full flex-col gap-4 overflow-auto p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
          Section settings
        </p>

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            Section name
          </label>
          <input
            value={editFormData.name}
            onChange={(e) => {
              const name = e.target.value;
              setFormData((prev) => ({ ...prev, name }));
              patchItem({ name: name || "New Section", label: name || "New Section" });
            }}
            placeholder="New Section"
            className="w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none transition placeholder:text-[#9CA3AF] focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
          />
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            Section type
          </p>
          <div className="flex gap-2">
            <ChoiceCard
              title="Normal"
              subtitle="Visual group only"
              selected={editFormData.sectionType === "normal"}
              selectedClass="border-[#3B82F6] bg-[#EFF6FF] text-[#1D4ED8]"
              onClick={() => {
                setFormData((prev) => ({ ...prev, sectionType: "normal" }));
                patchItem({ sectionType: "normal" });
              }}
            />
            <ChoiceCard
              title="Form step"
              subtitle="Separate page on public form"
              selected={editFormData.sectionType === "step"}
              selectedClass="border-[#22C55E] bg-[#F0FDF4] text-[#15803D]"
              onClick={() => {
                setFormData((prev) => ({ ...prev, sectionType: "step" }));
                patchItem({ sectionType: "step" });
              }}
            />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            Access
          </p>
          <div className="flex gap-2">
            <ChoiceCard
              title="All Staff"
              subtitle="Visible to everyone"
              selected={editFormData.access === "all"}
              selectedClass="border-[#2563EB] bg-[#2563EB] text-white"
              onClick={() => {
                setFormData((prev) => ({ ...prev, access: "all" }));
                patchItem({ access: "all", sectionAccess: "all" });
              }}
            />
            <ChoiceCard
              title="Restricted"
              subtitle="Limited roles only"
              selected={editFormData.access === "restricted"}
              selectedClass="border-[#A02448] bg-[#A02448] text-white"
              onClick={() => {
                setFormData((prev) => ({ ...prev, access: "restricted" }));
                patchItem({
                  access: "restricted",
                  sectionAccess: "restricted",
                });
              }}
            />
          </div>
          {editFormData.access === "restricted" ? (
            <p className="mt-2 rounded-lg border border-[#DDD6FE] bg-[#F5F3FF] px-2.5 py-2 text-[11px] leading-relaxed text-[#5B21B6]">
              Restricted — only allowed users can see this section.
            </p>
          ) : (
            <p className="mt-2 text-[11px] leading-relaxed text-[#6B7280]">
              Fields after this section (until the next section) belong to it.
            </p>
          )}
        </div>

        <div className="mt-auto pt-2">
          <YhButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => props.setInputId("")}
          >
            Done
          </YhButton>
        </div>
      </div>
    );
  }

  const updateCondition = (idx: number, updates: Partial<ConditionRule>) => {
    const next = editFormData.conditions.map((rule, i) =>
      i === idx ? { ...rule, ...updates } : rule
    );
    setFormData((prev) => ({ ...prev, conditions: next }));
    patchItem({ conditions: next });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex h-full flex-col gap-4 overflow-auto p-4"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
          Field type
        </p>
        <p className="mt-1 text-sm font-semibold text-[#111827]">
          {LABEL_MAP[editFormData.type] || editFormData.type || "—"}
        </p>
      </div>

      <YhInput
        label="Field Name"
        defaultValue={editFormData.name}
        error={errors.name ? "This field is required." : ""}
        {...register("name", {
          required: true,
          // Commit as the user types so the page-level Update Form saves the
          // latest value even when Apply is not clicked first.
          onChange: (e: any) => patchItem({ name: e.target.value }),
        })}
      />

      {!NO_PLACEHOLDER.has(editFormData.type) ? (
        <YhInput
          label="Placeholder"
          defaultValue={editFormData.placeholder}
          error={errors.placeholder ? "This field is required." : ""}
          {...register("placeholder", {
            required: true,
            onChange: (e: any) => patchItem({ placeholder: e.target.value }),
          })}
        />
      ) : null}

      {HAS_OPTIONS.has(editFormData.type) ? (
        <>
          <OptionsCreatable
            options={editFormData.options}
            onChange={(next) => {
              setFormData((prev) => ({ ...prev, options: next }));
              patchItem({ options: next });
            }}
          />
          {(editFormData.type === "select" ||
            editFormData.type === "checkbox") && (
            <label className="flex items-center gap-2 text-sm text-[#374151]">
              <input
                type="checkbox"
                checked={
                  editFormData.type === "checkbox"
                    ? true
                    : !!(props.items || []).find(
                        (i: any) => i.id === props.inputId
                      )?.multiple
                }
                disabled={editFormData.type === "checkbox"}
                onChange={(e) =>
                  patchItem({ multiple: e.target.checked })
                }
              />
              Allow multiple answers
            </label>
          )}
          {editFormData.type === "radio" && (
            <p className="text-[11px] text-[#6B7280]">
              Radio = single selection. Use Checkbox for multi-select.
            </p>
          )}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Option layout
            </p>
            <div className="flex gap-2">
              <ChoiceCard
                title="Chips"
                subtitle="Pill buttons (wrap)"
                selected={editFormData.choiceStyle === "chips"}
                selectedClass="border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, choiceStyle: "chips" }));
                  patchItem({ choiceStyle: "chips" });
                }}
              />
              <ChoiceCard
                title="Dropdown"
                subtitle="Classic select box"
                selected={editFormData.choiceStyle === "dropdown"}
                selectedClass="border-[#A02448] bg-[#F9F0F4] text-[#841C39]"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    choiceStyle: "dropdown",
                  }));
                  patchItem({ choiceStyle: "dropdown" });
                }}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#374151]">
            <input
              type="checkbox"
              checked={editFormData.allowOther}
              onChange={(e) => {
                const allowOther = e.target.checked;
                setFormData((prev) => {
                  let options = prev.options;
                  if (allowOther) {
                    const hasOther = options.some(
                      (o) => o.trim().toLowerCase() === "other"
                    );
                    if (!hasOther) options = [...options, "Other"];
                  }
                  return { ...prev, allowOther, options };
                });
                const item = (props.items || []).find(
                  (i: any) => i.id === props.inputId
                );
                let options = parseOptionsList(item?.options);
                if (allowOther) {
                  const hasOther = options.some(
                    (o) => o.trim().toLowerCase() === "other"
                  );
                  if (!hasOther) options = [...options, "Other"];
                }
                patchItem({ allowOther, options });
              }}
            />
            Allow &quot;Other&quot; with free-text input
          </label>
          <p className="text-[11px] text-[#6B7280]">
            When enabled, selecting Other shows a text box for the respondent
            to type their own answer.
          </p>
        </>
      ) : null}

      {editFormData.type === "project" ? (
        <>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Selection mode
            </p>
            <div className="flex gap-2">
              <ChoiceCard
                title="Single"
                subtitle="One project only"
                selected={editFormData.selectionMode === "single"}
                selectedClass="border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    selectionMode: "single",
                  }));
                  patchItem({ selectionMode: "single" });
                }}
              />
              <ChoiceCard
                title="Multiple"
                subtitle="Several projects"
                selected={editFormData.selectionMode === "multiple"}
                selectedClass="border-[#22C55E] bg-[#F0FDF4] text-[#15803D]"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    selectionMode: "multiple",
                  }));
                  patchItem({ selectionMode: "multiple" });
                }}
              />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Projects to show
            </p>
            <div className="flex gap-2">
              <ChoiceCard
                title="All projects"
                subtitle="Every org project"
                selected={editFormData.projectScope === "all"}
                selectedClass="border-[#841C39] bg-[#F9F0F4] text-[#4338CA]"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    projectScope: "all",
                    allowedProjectIds: [],
                  }));
                  patchItem({
                    projectScope: "all",
                    allowedProjectIds: [],
                  });
                }}
              />
              <ChoiceCard
                title="Selected only"
                subtitle="Pick a subset"
                selected={editFormData.projectScope === "selected"}
                selectedClass="border-[#A02448] bg-[#F5F3FF] text-[#5B21B6]"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    projectScope: "selected",
                  }));
                  patchItem({ projectScope: "selected" });
                }}
              />
            </div>
          </div>

          {editFormData.projectScope === "selected" ? (
            <YhMultiSelect
              label="Choose projects"
              placeholder="Select projects to show on form"
              options={(props.availableProjects || []).map((p: any) => ({
                label: p.label,
                value: String(p.id),
              }))}
              value={editFormData.allowedProjectIds}
              showSelectAll
              searchable
              showOptionIcon={false}
              maxVisibleChips={3}
              clearable
              onChange={(next) => {
                const ids = next.map((v) => String(v));
                setFormData((prev) => ({
                  ...prev,
                  allowedProjectIds: ids,
                }));
                patchItem({ allowedProjectIds: ids });
              }}
            />
          ) : (
            <p className="rounded-lg border border-[#F9F0F4] bg-[#F9F0F4] px-2.5 py-2 text-[11px] leading-relaxed text-[#4338CA]">
              Respondents will see all{" "}
              {(props.availableProjects || []).length || "your"} organization
              projects on the public form.
            </p>
          )}

          {editFormData.projectScope === "selected" &&
          !editFormData.allowedProjectIds.length ? (
            <p className="text-[11px] text-[#DC2626]">
              Select at least one project when using &quot;Selected only&quot;.
            </p>
          ) : null}
        </>
      ) : null}

      {editFormData.type === "rating" ? (
        <>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Rating style
            </p>
            <div className="flex gap-2">
              <ChoiceCard
                title="Emoji faces"
                subtitle="Animated mood scale"
                selected={editFormData.ratingStyle === "emoji"}
                selectedClass="border-[#22C55E] bg-[#F0FDF4] text-[#15803D]"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, ratingStyle: "emoji" }));
                  patchItem({ ratingStyle: "emoji" });
                }}
              />
              <ChoiceCard
                title="Stars"
                subtitle="Classic ★ rating"
                selected={editFormData.ratingStyle === "stars"}
                selectedClass="border-[#F59E0B] bg-[#FFFBEB] text-[#B45309]"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, ratingStyle: "stars" }));
                  patchItem({ ratingStyle: "stars" });
                }}
              />
            </div>
          </div>
          <YhInput
            label="Max rating"
            type="number"
            value={String(
              (props.items || []).find((i: any) => i.id === props.inputId)
                ?.maxRating || 5
            )}
            onChange={(e: any) => {
              const n = Number(e?.target?.value);
              patchItem({
                maxRating: Number.isFinite(n) && n > 0 ? Math.min(10, n) : 5,
              });
            }}
          />
        </>
      ) : null}

      {!NO_REQUIRED.has(editFormData.type) ? (
        <label className="flex items-center gap-2 text-sm text-[#374151]">
          <input
            type="checkbox"
            {...register("required", {
              onChange: (e: any) => patchItem({ required: !!e.target.checked }),
            })}
            defaultChecked={editFormData.required}
          />
          Required field
        </label>
      ) : null}

      {/* Conditions — SmartCMS AttributeEditor pattern */}
      <div className="rounded-xl border border-[#E5E7EB]">
        <button
          type="button"
          onClick={() => setConditionsOpen((o) => !o)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-left"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Conditions
            </p>
            <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
              Show, hide, or set values using rules
            </p>
          </div>
          <span className="text-[#9CA3AF]">{conditionsOpen ? "▴" : "▾"}</span>
        </button>

        {conditionsOpen ? (
          <div className="space-y-3 border-t border-[#F3F4F6] px-3 py-3">
            <div className="flex gap-2">
              {(["AND", "OR"] as const).map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      conditionOperator: op,
                    }));
                    patchItem({ conditionOperator: op });
                  }}
                  className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${
                    editFormData.conditionOperator === op
                      ? "border-[#841C39] bg-[#F9F0F4] text-[#841C39]"
                      : "border-[#E5E7EB] text-[#6B7280]"
                  }`}
                >
                  {op}
                </button>
              ))}
            </div>

            {editFormData.conditions.map((rule, idx) => (
              <div
                key={idx}
                className="space-y-2 rounded-lg border border-[#F3F4F6] bg-[#FAFBFC] p-2.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#6B7280]">
                    Rule {idx + 1}
                  </span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-[#DC2626]"
                    onClick={() => {
                      const next = editFormData.conditions.filter(
                        (_, i) => i !== idx
                      );
                      setFormData((prev) => ({ ...prev, conditions: next }));
                      patchItem({ conditions: next });
                    }}
                  >
                    Remove
                  </button>
                </div>
                <select
                  value={rule.action}
                  onChange={(e) =>
                    updateCondition(idx, {
                      action: e.target.value as ConditionRule["action"],
                    })
                  }
                  className="w-full rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs"
                >
                  <option value="show">Show this field when</option>
                  <option value="hide">Hide this field when</option>
                  <option value="set_value">Set value when</option>
                </select>
                <select
                  value={rule.fieldId}
                  onChange={(e) =>
                    updateCondition(idx, { fieldId: e.target.value })
                  }
                  className="w-full rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs"
                >
                  <option value="">Select field…</option>
                  {otherFields.map((f: any) => (
                    <option key={f.id} value={f.id}>
                      {f.name || LABEL_MAP[f.type] || f.type}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <select
                    value={rule.operator}
                    onChange={(e) =>
                      updateCondition(idx, { operator: e.target.value })
                    }
                    className="w-[40%] rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs"
                  >
                    <option value="equals">equals</option>
                    <option value="not_equals">not equals</option>
                    <option value="contains">contains</option>
                    <option value="empty">is empty</option>
                    <option value="not_empty">is not empty</option>
                  </select>
                  <input
                    value={rule.value}
                    onChange={(e) =>
                      updateCondition(idx, { value: e.target.value })
                    }
                    placeholder="Value"
                    className="w-[60%] rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs"
                  />
                </div>
                {rule.action === "set_value" ? (
                  <input
                    value={rule.setValue || ""}
                    onChange={(e) =>
                      updateCondition(idx, { setValue: e.target.value })
                    }
                    placeholder="Set this value"
                    className="w-full rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs"
                  />
                ) : null}
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                const next = [...editFormData.conditions, emptyCondition()];
                setFormData((prev) => ({ ...prev, conditions: next }));
                patchItem({ conditions: next });
              }}
              className="w-full rounded-lg border border-dashed border-[#D4B8C0] py-2 text-xs font-semibold text-[#841C39] transition hover:bg-[#F9F0F4]"
            >
              + Add condition
            </button>
          </div>
        ) : null}
      </div>

      <div className="mt-auto flex gap-2 pt-2">
        <YhButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => props.setInputId("")}
        >
          Done
        </YhButton>
        <YhButton type="submit" variant="primary" size="sm">
          Apply
        </YhButton>
      </div>
    </form>
  );
};

export default BuilderPage;
